import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiKey, FiPlus, FiSearch, FiInbox, FiTrash2, FiCopy } from "react-icons/fi";
import { toast } from "react-toastify";
import moment from "moment";
import { getApiTokenCatalog, getApiTokens } from "../../api/getapi";
import { createApiToken, revokeApiToken } from "../../api/postapi";
import "./ApiTokens.css";

const EXPIRY_OPTIONS = [
  { label: "30 days", value: "30" },
  { label: "90 days", value: "90" },
  { label: "1 year", value: "365" },
  { label: "Never", value: "" },
];

const parseScopes = (t) => (typeof t.scopes === "string" ? JSON.parse(t.scopes) : t.scopes || []);

const statusOf = (t) => {
  if (t.revoked_at) return "revoked";
  if (t.expires_at && moment(t.expires_at).isBefore(moment())) return "expired";
  return "active";
};
const statusLabel = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const errMsg = (e, fallback) => e?.response?.data?.error?.message || fallback;

const ApiTokens = () => {
  const [tokens, setTokens] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState("90");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [newToken, setNewToken] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getApiTokens();
      setTokens(res?.data?.data || []);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load tokens"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    getApiTokenCatalog()
      .then((res) => setCatalog(res?.data?.tools || []))
      .catch(() => toast.error("Failed to load tool catalog"));
  }, []);

  const accessByTool = useMemo(() => Object.fromEntries(catalog.map((t) => [t.name, t.access])), [catalog]);

  const groups = useMemo(() => {
    const map = {};
    catalog.forEach((t) => (map[t.group] = map[t.group] || []).push(t));
    return map;
  }, [catalog]);

  const rows = useMemo(
    () =>
      tokens.filter((t) => {
        const q = search.trim().toLowerCase();
        const matchesSearch = !q || t.name?.toLowerCase().includes(q) || t.owner_name?.toLowerCase().includes(q);
        return matchesSearch && (statusFilter === "all" || statusOf(t) === statusFilter);
      }),
    [tokens, search, statusFilter]
  );

  const toggleTool = (toolName) => {
    const next = new Set(selected);
    next.has(toolName) ? next.delete(toolName) : next.add(toolName);
    setSelected(next);
  };
  const setMany = (items, on) => {
    const next = new Set(selected);
    items.forEach((t) => (on ? next.add(t.name) : next.delete(t.name)));
    setSelected(next);
  };

  const openCreate = () => {
    setName("");
    setExpiry("90");
    setSelected(new Set());
    setShowCreate(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selected.size === 0) return toast.error("Select at least one tool");
    setSaving(true);
    try {
      const res = await createApiToken({ name, scopes: [...selected], expiresInDays: expiry });
      setShowCreate(false);
      setNewToken(res.data.token);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Failed to create token"));
    }
    setSaving(false);
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke "${t.name}"? Anything using it will stop working immediately.`)) return;
    try {
      await revokeApiToken(t.id);
      toast.success("Token revoked");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Failed to revoke token"));
    }
  };

  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  const apiUrl = process.env.REACT_APP_API_URL || "<api-admin url>";
  const writeTools = catalog.filter((t) => t.access === "write");
  const readTools = catalog.filter((t) => t.access === "read");

  return (
    <div className="container-fluid">
      <div className="at-page-header">
        <div className="at-page-identity">
          <span className="at-page-icon">
            <FiKey />
          </span>
          <h1 className="at-page-title">API Tokens</h1>
          <span className="at-page-subtitle">
            Long-lived tokens for the Techbazaar MCP server, limited to the tools you grant
          </span>
        </div>
        <button type="button" className="at-btn at-btn-primary" onClick={openCreate}>
          <FiPlus /> Create Token
        </button>
      </div>

      <div className="at-toolbar">
        <div className="at-search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by token name or owner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="at-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
        <span className="at-result-count">
          {rows.length} token{rows.length !== 1 ? "s" : ""}
        </span>
      </div>

      {rows.length > 0 ? (
        <div className="at-table-wrapper">
          <div className="table-responsive">
            <table className="table at-table align-middle">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Owner</th>
                  <th>Tools</th>
                  <th>Expires</th>
                  <th>Last Used</th>
                  <th>Status</th>
                  <th style={{ width: "80px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const scopes = parseScopes(t);
                  const writes = scopes.filter((s) => accessByTool[s] === "write").length;
                  const reads = scopes.length - writes;
                  const status = statusOf(t);
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="at-name">{t.name}</div>
                        <span className="at-token-code">{t.token_prefix}…</span>
                        <div className="at-sub">Created {moment(t.created_at).format("DD MMM YYYY")}</div>
                      </td>
                      <td className="at-cell-muted">{t.owner_name || "—"}</td>
                      <td title={scopes.join(", ")}>
                        <span className="at-chip at-chip-read">{reads} read</span>
                        {writes > 0 && <span className="at-chip at-chip-write">{writes} write</span>}
                      </td>
                      <td className="at-cell-muted">
                        {t.expires_at ? moment(t.expires_at).format("DD MMM YYYY") : "Never"}
                      </td>
                      <td className="at-cell-muted">{t.last_used_at ? moment(t.last_used_at).fromNow() : "Never"}</td>
                      <td>
                        <span className={`at-status-pill at-status-${status}`}>{statusLabel(status)}</span>
                      </td>
                      <td>
                        {!t.revoked_at && (
                          <button className="at-action-btn danger" onClick={() => revoke(t)} title="Revoke">
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="at-empty-state">
          <div className="at-empty-icon">
            <FiInbox size={28} />
          </div>
          <div className="at-empty-title">{loading ? "Loading tokens..." : "No tokens found"}</div>
          {!loading && (
            <div className="at-empty-subtitle">
              {tokens.length > 0 ? "Try adjusting your search or filter." : "Create your first token to connect the MCP server."}
            </div>
          )}
          {!loading && tokens.length === 0 && (
            <button className="at-btn at-btn-primary mt-3" onClick={openCreate}>
              <FiPlus /> Create Token
            </button>
          )}
        </div>
      )}

      {/* Create token. The form is inside the body (not wrapping the modal sections) so only the body scrolls. */}
      <Modal show={showCreate} onHide={() => !saving && setShowCreate(false)} size="lg" centered scrollable dialogClassName="at-modal">
        <Modal.Header closeButton>
          <Modal.Title>Create API token</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="create-token-form" onSubmit={submit}>
            <div className="at-form-grid">
              <div>
                <label className="at-field-label" htmlFor="token-name">Name</label>
                <input
                  id="token-name"
                  className="at-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Marketing analysis (Claude)"
                  maxLength={120}
                  required
                />
              </div>
              <div>
                <label className="at-field-label" htmlFor="token-expiry">Expires</label>
                <select id="token-expiry" className="at-input" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.label} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="at-tools-head">
              <span className="at-field-label mb-0">Tools this token may use ({selected.size} selected)</span>
              <span>
                <button type="button" className="at-link-btn" onClick={() => setMany(readTools, true)}>
                  Select all read-only
                </button>
                {" · "}
                <button type="button" className="at-link-btn" onClick={() => setSelected(new Set())}>
                  Clear
                </button>
              </span>
            </div>

            {Object.entries(groups).map(([group, items]) => {
              const count = items.filter((t) => selected.has(t.name)).length;
              return (
                <div key={group} className="at-group">
                  <div className="at-group-head">
                    <label className="at-group-title">
                      <input
                        type="checkbox"
                        style={{ accentColor: "#8834ff" }}
                        checked={count === items.length}
                        onChange={(e) => setMany(items, e.target.checked)}
                      />
                      {group}
                    </label>
                    <span className="at-group-count">{count}/{items.length}</span>
                  </div>
                  <div className="at-tool-grid">
                    {items.map((t) => (
                      <label key={t.name} className="at-tool">
                        <input type="checkbox" checked={selected.has(t.name)} onChange={() => toggleTool(t.name)} />
                        <span className="at-tool-text">
                          {t.description}{" "}
                          {t.access === "write" && <span className="at-chip at-chip-write">changes live data</span>}
                          {t.notifies && <span className="at-chip at-chip-notify">sends SMS</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            {writeTools.length > 0 && (
              <div className="at-hint">Prefer read-only tools. Write tools can change what customers see.</div>
            )}
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="at-btn at-btn-ghost" onClick={() => setShowCreate(false)} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="create-token-form" className="at-btn at-btn-primary" disabled={saving || selected.size === 0}>
            {saving ? "Creating..." : `Create token (${selected.size})`}
          </button>
        </Modal.Footer>
      </Modal>

      {/* One-time token reveal */}
      <Modal show={!!newToken} onHide={() => setNewToken(null)} backdrop="static" centered scrollable dialogClassName="at-modal">
        <Modal.Header>
          <Modal.Title>Copy your token now</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="at-warning">This is the only time the token is shown. Store it securely.</div>
          <div className="at-token-box">{newToken}</div>
          <button type="button" className="at-btn at-btn-ghost mt-2" onClick={() => copy(newToken)}>
            <FiCopy /> Copy token
          </button>
          <div className="at-field-label mt-4 mb-0">Add to Claude Code</div>
          <pre className="at-snippet">{`claude mcp add techbazaar \\
  -e TECHBAZAAR_API_URL=${apiUrl} \\
  -e TECHBAZAAR_API_TOKEN=${newToken} \\
  -- node <path>/techbazaar-mcp/src/index.js`}</pre>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="at-btn at-btn-primary" onClick={() => setNewToken(null)}>
            I've saved it
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApiTokens;
